/**
 * Reading a form's `errors` object — the `{ field: "message" | "" }` map every
 * validateForm in this app writes through UseForm's setErrors.
 *
 * A "section" is one tab (or block) of a form: `{ label, fields: [...] }`, listed in
 * the same order as the layout/Tabs header so section i IS tab i. A field name ending
 * in "_" is a prefix, for keys a form generates per row (`unitMin_` → `unitMin_0`, …).
 *
 * Pure on purpose: the banner, the tab badges and the jump-to-field all derive from
 * these, so they can never disagree about how many errors there are or where.
 */

const isMessage = (v) => typeof v === "string" && v.trim() !== "";

const fieldMatches = (pattern, field) =>
  pattern.endsWith("_") ? field.startsWith(pattern) : pattern === field;

/** The section a field lives in, or -1 when no section claims it. */
export const sectionIndexOf = (field, sections = []) =>
  sections.findIndex((s) => (s.fields || []).some((p) => fieldMatches(p, field)));

/**
 * Every field that currently has a message, in the order a person should fix them:
 * by section (tab order), then in the order validateForm wrote the keys — which is
 * the order the fields appear on screen. Fields no section claims come last.
 */
export const errorEntries = (errors, sections = []) => {
  const entries = Object.entries(errors || {})
    .filter(([, message]) => isMessage(message))
    .map(([field, message], order) => {
      const sectionIndex = sectionIndexOf(field, sections);
      return {
        field,
        message,
        order,
        sectionIndex,
        sectionLabel: sectionIndex > -1 ? sections[sectionIndex].label : "",
      };
    });
  const rank = (e) => (e.sectionIndex > -1 ? e.sectionIndex : Number.MAX_SAFE_INTEGER);
  return entries.sort((a, b) => rank(a) - rank(b) || a.order - b.order);
};

/** How many errors each section holds, aligned with the Tabs header. */
export const errorCountsBySection = (errors, sections = []) => {
  const counts = sections.map(() => 0);
  errorEntries(errors, sections).forEach((e) => {
    if (e.sectionIndex > -1) counts[e.sectionIndex] += 1;
  });
  return counts;
};
